import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { plural } from '@/features/proactive-agents/plural';
import type {
  UserAction,
  UserAgentChange,
  UserFilter,
} from '@/features/proactive-agents/proactive-agents-types';
import {
  restartActionLabel,
  USER_ACTIONS,
} from '@/features/proactive-agents/userActions';

// Same limit as the backend
const MAX_SPREAD_MINUTES = 24 * 60;

const parseSpreadMinutes = (text: string) =>
  /^\d+$/.test(text) && Number(text) <= MAX_SPREAD_MINUTES
    ? Number(text)
    : null;

const ApplyNowFields = ({
  applyNow,
  onApplyNowChange,
  spreadMinutes,
  onSpreadMinutesChange,
  spreadIsValid,
}: {
  applyNow: boolean;
  onApplyNowChange: (applyNow: boolean) => void;
  spreadMinutes: string;
  onSpreadMinutesChange: (spreadMinutes: string) => void;
  spreadIsValid: boolean;
}) => {
  const hintId = useId();
  return (
    <div className="grid gap-3 text-sm">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={applyNow}
          onChange={(event) => onApplyNowChange(event.target.checked)}
        />
        Apply now
      </label>
      {applyNow ? (
        <>
          <p className="text-muted-foreground">
            The default agent starts now: an active chat of it stays, a paused
            one resumes, and otherwise a new chat starts. For a user who is not
            internal, their other built-in agents are then stopped. A stopped
            chat must be deleted before its agent can start again.
          </p>
          <label className="grid gap-2">
            Spread new chats&apos; first turns over (minutes)
            <Input
              type="number"
              min={0}
              max={MAX_SPREAD_MINUTES}
              value={spreadMinutes}
              onChange={(event) => onSpreadMinutesChange(event.target.value)}
              aria-invalid={!spreadIsValid || undefined}
              aria-describedby={hintId}
            />
          </label>
          <p
            id={hintId}
            className={
              spreadIsValid ? 'text-muted-foreground' : 'text-destructive'
            }
          >
            {spreadIsValid
              ? 'A first turn costs the most, so spreading avoids a burst.'
              : `Enter whole minutes from 0 to ${MAX_SPREAD_MINUTES}.`}
          </p>
        </>
      ) : (
        <p className="text-muted-foreground">
          Each user gets it at their agents&apos; next run, normally the daily
          run early in the morning, if they were active in the last week and may
          have proactive agents. For a user who is not internal, that run also
          stops their other built-in agents. Users whose chat of it is paused or
          stopped are skipped, as are internal users whose chat of it was
          deleted; the results say what to do.
        </p>
      )}
    </div>
  );
};

const describeAction = (
  action: UserAction,
  agent: string,
  codeDefault: string,
  agentCanBeDefault: boolean
): string => {
  switch (action) {
    case 'set-default':
      return `This sets an override: they keep ${agent} even if the code default changes, until the override is cleared or an internal user sets another default in their settings.`;
    case 'clear-default':
      return `Their default agent becomes the code default, ${codeDefault}.`;
    case 'pause':
      return 'Its routines pause with it. If apply now delayed its first turn, that turn still runs.';
    case 'resume':
      return "Its routines resume with it. For a user who is not internal and has another default agent, their agents' next run stops it, and a stopped chat must be deleted before it can run again.";
    case 'delete':
      return (
        'This also deletes the routines the agent created and the cards they made. It cannot be undone. ' +
        (agentCanBeDefault
          ? `If ${agent} is the default agent of a user who is not internal, it starts again at their next daily run, once they have been active in the past week. ` +
            `For an internal user it starts again only with ${restartActionLabel(agent, codeDefault)} and apply now, or Run now in their settings.`
          : 'For an internal user it starts again only with Run now in their settings.')
      );
  }
};

export const ChangeUsersDialog = ({
  action,
  agent,
  codeDefault,
  agentCanBeDefault,
  filter,
  userCount,
  previewChanged,
  canConfirm,
  onConfirm,
  onCancel,
}: {
  action: UserAction;
  agent: string;
  codeDefault: string;
  agentCanBeDefault: boolean;
  filter: UserFilter;
  // How many users the preview says the change acts on
  userCount: number;
  // The preview's agent or users differ from those the dialog was opened for
  previewChanged: boolean;
  canConfirm: boolean;
  onConfirm: (change: UserAgentChange) => void;
  onCancel: () => void;
}) => {
  const [applyNow, setApplyNow] = useState(true);
  // Defaults to 30 so a large change does not start every first turn at once
  const [spreadMinutes, setSpreadMinutes] = useState('30');
  const setsDefault = action === 'set-default' || action === 'clear-default';
  // Null while the field is invalid, and 0 when apply now is off since the field is hidden
  const appliedSpreadMinutes = applyNow ? parseSpreadMinutes(spreadMinutes) : 0;

  const handleConfirm = () => {
    if (!setsDefault) {
      onConfirm({ action, agent, filter });
      return;
    }
    if (appliedSpreadMinutes !== null)
      onConfirm({
        action,
        agent,
        filter,
        applyNow,
        spreadMinutes: appliedSpreadMinutes,
      });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {USER_ACTIONS[action].name(agent)} for {plural(userCount, 'user')}?
          </DialogTitle>
          <DialogDescription>
            {describeAction(action, agent, codeDefault, agentCanBeDefault)}
          </DialogDescription>
        </DialogHeader>
        {previewChanged ? (
          <p role="alert" className="text-sm text-destructive">
            The preview changed. Cancel and check it again.
          </p>
        ) : null}
        {setsDefault ? (
          <ApplyNowFields
            applyNow={applyNow}
            onApplyNowChange={setApplyNow}
            spreadMinutes={spreadMinutes}
            onSpreadMinutesChange={setSpreadMinutes}
            spreadIsValid={appliedSpreadMinutes !== null}
          />
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={action === 'delete' ? 'destructive' : 'default'}
            disabled={
              !canConfirm || previewChanged || appliedSpreadMinutes === null
            }
            onClick={handleConfirm}
          >
            {USER_ACTIONS[action].label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
